import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Card } from '@src/components';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import {
  readImageFile,
  LOGO_MAX,
  FAVICON_MAX,
  buildBrandingFromTenant,
  setCachedBranding,
  applyDocumentBranding,
} from '@src/utils/branding';

const BrandingSettings = () => {
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.theme);

  // data-URIs picked by the user this session (only these get submitted)
  const [logoData, setLogoData] = useState(null);
  const [faviconData, setFaviconData] = useState(null);
  const [loading, setLoading] = useState(false);

  const logoPreview = logoData || theme.logo;
  const faviconPreview = faviconData || theme.favicon;

  const notiError = (message) =>
    dispatch(actions.noti.push({ severity: 'error', message }));

  const handlePickLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    try {
      const dataUri = await readImageFile(file, LOGO_MAX);
      setLogoData(dataUri);
      // live preview: update the sidebar logo immediately
      dispatch(actions.theme.setBranding({ logo: dataUri }));
    } catch (err) {
      notiError('Logo phải là ảnh và ≤ 1MB');
    }
  };

  const handlePickFavicon = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      const dataUri = await readImageFile(file, FAVICON_MAX);
      setFaviconData(dataUri);
      // live preview: update the browser tab icon immediately
      dispatch(actions.theme.setBranding({ favicon: dataUri }));
      applyDocumentBranding({ ...theme, favicon: dataUri });
    } catch (err) {
      notiError('Favicon phải là ảnh và ≤ 300KB');
    }
  };

  const handleSubmit = async () => {
    const assets = {};
    if (logoData) assets.logoBase64 = logoData;
    if (faviconData) assets.faviconBase64 = faviconData;
    if (Object.keys(assets).length === 0) {
      notiError('Bạn chưa chọn logo hoặc favicon mới');
      return;
    }

    setLoading(true);
    const res = await apis.tenant.updateTenant({ assets });
    setLoading(false);

    if (!res?.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          code: res?.code || 0,
          message: res?.message,
        }),
      );
      return;
    }

    const branding = buildBrandingFromTenant(res.result);
    dispatch(actions.theme.setBranding(branding));
    setCachedBranding(branding);
    applyDocumentBranding(branding);
    setLogoData(null);
    setFaviconData(null);
    dispatch(
      actions.noti.push({
        severity: 'success',
        message: 'Đã cập nhật thương hiệu',
      }),
    );
  };

  return (
    <Card title="Thương hiệu">
      <Stack spacing={3} sx={{ p: 1 }}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Logo (≤ 1MB)
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            {logoPreview && (
              <img
                src={logoPreview}
                alt="logo"
                style={{ height: 56, maxWidth: 200, objectFit: 'contain' }}
              />
            )}
            <Button variant="outlined" component="label" size="small">
              Chọn logo
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={handlePickLogo}
              />
            </Button>
          </Stack>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Favicon (≤ 300KB)
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            {faviconPreview && (
              <img
                src={faviconPreview}
                alt="favicon"
                style={{ height: 32, width: 32, objectFit: 'contain' }}
              />
            )}
            <Button variant="outlined" component="label" size="small">
              Chọn favicon
              <input
                hidden
                type="file"
                accept="image/*,.ico"
                onChange={handlePickFavicon}
              />
            </Button>
          </Stack>
        </Box>

        <Box>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || (!logoData && !faviconData)}
          >
            Lưu
          </Button>
        </Box>
      </Stack>
    </Card>
  );
};

export default BrandingSettings;
